import { useTranslation } from "react-i18next";
import { IconChevronDown } from "@tabler/icons-react";
import { GPT_ANSWER_CLASS } from "../../../domains/gpt/answer";
import ContentMarkdown from "../../ContentMarkdown";

/**
 * The pre-answer of an assistant run (docs/juisci-gpt-pre-answer.md): a short
 * Markdown text a model writes from its own knowledge while the research runs.
 *
 * - `live`: the run researches, and its sourced answer will replace the text (REQ-06).
 * - `unverified`: the run failed or was cancelled before its answer (REQ-09).
 * - `folded`: the answer replaced it, and it stays in a closed fold at the top of the answer (REQ-07).
 */
export function PreAnswer({
  text,
  view,
}: {
  text: string;
  view:
    | { kind: "live"; isWriting: boolean }
    | { kind: "unverified" }
    | { kind: "folded" };
}): React.JSX.Element {
  const { t } = useTranslation();
  // It cites nothing, so its links stay plain anchors.
  const body = (
    <ContentMarkdown
      className={
        view.kind === "live" && view.isWriting
          ? GPT_ANSWER_CLASS + " typing"
          : GPT_ANSWER_CLASS
      }
      math={false}
      linkVariant="anchor"
    >
      {text}
    </ContentMarkdown>
  );
  switch (view.kind) {
    case "live":
      return (
        <div className="flex flex-col gap-2">
          <p className="my-0 font-dm-sans text-footnote text-ds-slate-500">
            {t(
              "Checking the sources. The sourced answer will replace this first answer."
            )}
          </p>
          {body}
        </div>
      );
    case "unverified":
      return (
        <div className="flex flex-col gap-2">
          <p className="my-0 font-dm-sans text-footnote text-ds-slate-500">
            {t("First answer, not checked against the sources")}
          </p>
          {body}
        </div>
      );
    case "folded":
      return (
        <details className="group">
          <summary className="flex w-fit cursor-pointer list-none items-center gap-1 font-dm-sans text-footnote text-ds-slate-500 [&::-webkit-details-marker]:hidden">
            {t("First answer, before the source check")}
            <IconChevronDown
              stroke={1.5}
              className="size-4 shrink-0 transition-transform duration-150 ease-out group-open:rotate-180"
            />
          </summary>
          {body}
        </details>
      );
    default:
      throw new Error(view satisfies never);
  }
}
